import type { PricingList } from 'api/types/pricing.types';
import { useConfig } from 'context/app-config';
import {
  selectDriverTip,
  selectisPremiumTimeSlot,
  selectIsShoeCareSelected,
  selectIsUrgentOrder,
  selectIsUrgentOrderInServiceLines,
  selectPremiumTimeSlotInServiceLines,
} from 'pages/newOrder/newOrder.selectors';
import { usePricing } from 'pages/queries/pricing.queries';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCustomer } from 'redux-stores/slices/authSlice';
import { useBillEstimator } from 'store/cart-estimator';

function useEstimatorCalculationProps() {
  const customer = useSelector(selectCustomer);
  const { data: pricing } = usePricing();
  const currency = pricing?.currency ?? 'AED';
  const driverTip = useSelector(selectDriverTip);

  const isShoeCareSelected = useSelector(selectIsShoeCareSelected);
  const premiumTimeSlotStatuses = useSelector(selectPremiumTimeSlotInServiceLines);
  const config = useConfig();
  const charges = pricing?.billingConstants.charges || ({} as PricingList.Charges);
  const urgentOrderStatuses = useSelector(selectIsUrgentOrderInServiceLines);
  const urgentOrderStatus = useSelector(selectIsUrgentOrder);
  const premiumTimeSlotsWithoutServiceLine = useSelector(selectisPremiumTimeSlot);

  const { totalLaundryBill = 0, totalEstimatedBill = 0, totalEstimatedBillBeforeTax = 0 } = useBillEstimator();

  const urgentDeliveries = useMemo(() => {
    if (config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs) {
      return (
        Object.entries(urgentOrderStatuses)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, isUrgent]) => isUrgent)
          .map(([serviceLine, isUrgent]) => ({
            serviceLine,
            isUrgent,
          }))
      );
    }

    if (urgentOrderStatus) {
      return [{ serviceLine: 'Laundry', isUrgent: true }];
    }

    return [];
  }, [urgentOrderStatuses, config, urgentOrderStatus]);

  const premiumTimeSlots = useMemo(() => {
    if (config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs) {
      return (
        Object.entries(premiumTimeSlotStatuses)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_, isPremium]) => isPremium)
          .map(([serviceLine, isUrgent]) => ({
            serviceLine,
            isUrgent,
          }))
      );
    }

    if (premiumTimeSlotsWithoutServiceLine) {
      return [{ serviceLine: 'Laundry', isUrgent: true }];
    }

    return [];
  }, [premiumTimeSlotStatuses, config, premiumTimeSlotsWithoutServiceLine]);

  const isDiscountAvailable = charges.discountedDelivery !== undefined;

  const primiumTimeSlotPrice = useMemo(() => {
    if (isDiscountAvailable) {
      return charges?.service?.discountedPriority ?? 0;
    }

    return charges?.service?.priority ?? 0;
  }, [charges?.service?.discountedPriority, charges?.service?.priority, isDiscountAvailable]);

  const urgentFee = useMemo(
    () => (totalLaundryBill > 0 ? urgentDeliveries.length * (totalLaundryBill / 2) : 0),
    [totalLaundryBill, urgentDeliveries?.length]
  );

  const premiumSlotFee = useMemo(() => premiumTimeSlots.length * primiumTimeSlotPrice, [premiumTimeSlots, primiumTimeSlotPrice]);

  const plusCustomerDiscount = useMemo(() => {
    if (!customer?.isPlus) return 0;

    const discount = totalEstimatedBill * 0.1; // Calculate 10% discount
    return discount > 10 ? 10 : discount; // If discount is greater than 10, cap it to 10
  }, [customer, totalEstimatedBill]);

  const subTotal = useMemo(
    () => totalLaundryBill + urgentFee + premiumSlotFee + (charges?.delivery ?? 0),
    [totalLaundryBill, charges?.delivery, urgentFee, premiumSlotFee]
  );

  const totalDifference = useMemo(() => {
    const difference = charges.minimumBeforeVat - subTotal;
    return difference > 0 ? difference : 0;
  }, [charges.delivery, charges.minimumBeforeVat, premiumSlotFee, totalLaundryBill, urgentFee]);

  const isEstimatorUsed = useMemo(() => totalLaundryBill > 0, [totalLaundryBill]);

  const vatAmount = useMemo(() => (subTotal + totalDifference) * charges.vatPercentage, [charges.vatPercentage, totalEstimatedBill, totalDifference]);

  const totalEstimatedBillAfterDiscounts = useMemo(
    () => subTotal + totalDifference + vatAmount - plusCustomerDiscount + (driverTip ?? 0),
    [totalEstimatedBill, totalDifference, plusCustomerDiscount, driverTip, urgentFee, premiumSlotFee]
  );

  const totalBeforeVATCalculated = useMemo(() => subTotal + totalDifference, [totalEstimatedBillBeforeTax, totalDifference, vatAmount]);

  const totals = {
    totalLaundryAmount: totalLaundryBill ?? 0,
    deliveryFee: charges.delivery ?? 0,
    totalEstimatedBillBeforeTax: totalBeforeVATCalculated ?? 0,
    totalEstimatedBill: totalEstimatedBillAfterDiscounts ?? 0,
    totalEstimatedBillAfterDiscounts: totalEstimatedBillAfterDiscounts ?? 0,
    plusCustomerDiscount: plusCustomerDiscount ?? 0,
    vatAmount: vatAmount ?? 0,
    urgentFee: urgentFee ?? 0,
    totalDifference: totalDifference ?? 0,
  };

  return {
    pricing,
    urgentDeliveries,
    premiumTimeSlots,
    isShowUrgentAmount: totalLaundryBill > 0 && !isShoeCareSelected,
    currency,
    isEstimatorUsed,
    primiumTimeSlotPrice,
    driverTip,
    ...totals,
  };
}

export { useEstimatorCalculationProps };
